import React, { useState, useRef, createElement } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { XIcon, MailIcon, SmartphoneIcon, LinkIcon, CheckIcon, UserIcon, EyeOffIcon, SendIcon, DollarSignIcon, QrCodeIcon, DownloadIcon, CalendarIcon, PercentIcon, BuildingIcon, ArrowRightIcon, ArrowLeftIcon, CheckCircleIcon } from 'lucide-react';
import { formatCurrencyAmount } from '../../../theme/tokens';
import { Recipient, Supplier, Installment } from './InstallmentPayment';
import { QRCodeSVG } from 'qrcode.react';
interface ShareLinkModalProps {
  onClose: () => void;
  splitType?: 'dates' | 'recipients' | 'commission' | null;
  recipients?: Recipient[];
  installments?: Installment[];
  suppliers?: Supplier[];
  totalAmount?: string;
  commissionType?: 'percentage' | 'fixed';
}
export function ShareLinkModal({
  onClose,
  splitType = null,
  recipients = [],
  installments = [],
  suppliers = [],
  totalAmount = '0',
  commissionType = 'percentage'
}: ShareLinkModalProps) {
  const {
    t
  } = useTranslation('payments');
  const [step, setStep] = useState<'preview' | 'share'>(() => {
    // Recipients mode goes straight to share (multi-recipient view)
    // No splitType (standard) also goes straight to share
    if (splitType === 'recipients' || !splitType) return 'share';
    // Schedule and commission start with preview
    return 'preview';
  });
  const [selectedOption, setSelectedOption] = useState<'email' | 'sms' | 'copy' | 'qrcode' | null>(null);
  const [recipientInput, setRecipientInput] = useState('');
  const [copied, setCopied] = useState(false);
  const [qrDownloaded, setQrDownloaded] = useState(false);
  const qrCodeRef = useRef<HTMLDivElement>(null);
  // For multiple recipients
  const [recipientContactMethods, setRecipientContactMethods] = useState<Record<number, 'email' | 'sms' | 'both'>>({});
  const [copiedRecipientId, setCopiedRecipientId] = useState<number | null>(null);
  const [recipientsViewTotalSettings, setRecipientsViewTotalSettings] = useState<Record<number, boolean>>(() => {
    const initialSettings: Record<number, boolean> = {};
    recipients.forEach(r => {
      initialSettings[r.id] = false;
    });
    return initialSettings;
  });
  const hasMultipleRecipients = splitType === 'recipients' && recipients.length > 0;
  const handleCopyLink = (recipientId?: number) => {
    const linkToCopy = recipientId ? `https://payment.example.com/pay/${recipientId}-abc123${recipientsViewTotalSettings[recipientId] ? '?showTotal=true' : ''}` : 'https://payment.example.com/pay/abc123';
    navigator.clipboard.writeText(linkToCopy);
    if (recipientId) {
      setCopiedRecipientId(recipientId);
      setTimeout(() => setCopiedRecipientId(null), 2000);
    } else {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };
  const handleDownloadQR = () => {
    if (!qrCodeRef.current) return;
    const canvas = qrCodeRef.current.querySelector('canvas');
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = 'payment-qrcode.png';
    link.href = canvas.toDataURL('image/png');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setQrDownloaded(true);
    setTimeout(() => setQrDownloaded(false), 2000);
  };
  const handleSend = () => {
    onClose();
  };
  const getContactMethod = (recipient: Recipient): 'email' | 'sms' | 'both' => {
    if (recipientContactMethods[recipient.id]) {
      return recipientContactMethods[recipient.id];
    }
    if (recipient.email && recipient.phone) return 'both';
    if (recipient.email) return 'email';
    if (recipient.phone) return 'sms';
    return 'email';
  };
  const setContactMethod = (recipientId: number, method: 'email' | 'sms' | 'both') => {
    setRecipientContactMethods(prev => ({
      ...prev,
      [recipientId]: method
    }));
  };
  const toggleRecipientTotalView = (recipientId: number) => {
    setRecipientsViewTotalSettings(prev => ({
      ...prev,
      [recipientId]: !prev[recipientId]
    }));
  };
  // Calculate commission values
  const totalAmountNum = parseFloat(totalAmount) || 0;
  const totalCommission = suppliers.reduce((sum, s) => {
    if (commissionType === 'percentage') {
      return sum + totalAmountNum * (parseFloat(s.percentage || '0') / 100);
    }
    return sum + (parseFloat(s.amount) || 0);
  }, 0);
  const businessShare = totalAmountNum - totalCommission;
  const getModalTitle = () => {
    if (step === 'preview') {
      if (splitType === 'dates') return 'Payment schedule preview';
      if (splitType === 'commission') return 'Commission split preview';
    }
    if (hasMultipleRecipients) return 'Share payment link';
    return 'Share payment link';
  };
  // ─── SCHEDULE PREVIEW ───
  const renderSchedulePreview = () => <div className="space-y-4" data-id="element-7757">
      <p className="text-sm text-gray-600" data-id="element-7758">
        {t('components.renderSchedulePreview.review_the_payment_schedule_before_creating')}
      </p>

      <div className="border border-gray-200 rounded-xl overflow-hidden" data-id="element-7759">
        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200" data-id="element-7760">
          <div className="grid grid-cols-12 gap-3 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7761">
            <div className="col-span-1" data-id="element-7762">#</div>
            <div className="col-span-3" data-id="element-7763">
              {t('components.BulkPaymentPreview.amount')}
            </div>
            <div className="col-span-3" data-id="element-7764">
              {t('components.InstallmentPayment.due_date')}
            </div>
            <div className="col-span-3" data-id="element-7765">
              {t('components.InstallmentPayment.send_link_on')}
            </div>
            <div className="col-span-2" data-id="element-7766">
              {t('components.BulkPaymentList.status')}
            </div>
          </div>
        </div>
        <div className="divide-y divide-gray-100" data-id="element-7767">
          {installments.map((inst, index) => <div key={inst.id} className="px-4 py-3.5" data-id="element-7768">
              <div className="grid grid-cols-12 gap-3 items-center" data-id="element-7769">
                <div className="col-span-1" data-id="element-7770">
                  <div className="w-6 h-6 flex items-center justify-center bg-[#FFC70A]/10 text-[#B28B07] rounded-full font-bold text-xs" data-id="element-7771">
                    {index + 1}
                  </div>
                </div>
                <div className="col-span-3" data-id="element-7772">
                  <span className="text-sm font-semibold text-gray-900" data-id="element-7773">
                    {formatCurrencyAmount(parseFloat(inst.amount || '0'), {
                  fractionDigits: 2
                })}
                  </span>
                  {inst.description && <p className="text-xs text-gray-500 mt-0.5" data-id="element-7774">
                      {inst.description}
                    </p>}
                </div>
                <div className="col-span-3" data-id="element-7775">
                  <div className="flex items-center text-sm text-gray-700" data-id="element-7776">
                    <CalendarIcon size={13} className="mr-1.5 text-gray-400" data-id="element-7777" />
                    {inst.dueDate || 'Not set'}
                  </div>
                </div>
                <div className="col-span-3" data-id="element-7778">
                  <div className="flex items-center text-sm text-gray-700" data-id="element-7779">
                    <SendIcon size={13} className="mr-1.5 text-gray-400" data-id="element-7780" />
                    {inst.sendLinkDate || 'Auto'}
                  </div>
                </div>
                <div className="col-span-2" data-id="element-7781">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200" data-id="element-7782">
                    {t('components.renderSchedulePreview.pending')}
                  </span>
                </div>
              </div>
            </div>)}
        </div>
        <div className="bg-gray-50 px-4 py-3 border-t border-gray-200" data-id="element-7783">
          <div className="flex justify-between items-center" data-id="element-7784">
            <span className="text-sm font-medium text-gray-600" data-id="element-7785">
              {t('components.renderSchedulePreview.total')}
            </span>
            <span className="text-sm font-bold text-gray-900" data-id="element-7786">
              {formatCurrencyAmount(installments.reduce((sum, i) => sum + parseFloat(i.amount || '0'), 0), {
              fractionDigits: 2
            })}
            </span>
          </div>
        </div>
      </div>

      <div className="bg-[#FFC70A]/5 border border-[#FFC70A]/20 rounded-xl p-4" data-id="element-7787">
        <div className="flex items-start gap-3" data-id="element-7788">
          <CalendarIcon size={18} className="text-[#B28B07] mt-0.5 flex-shrink-0" aria-hidden="true" data-id="element-7789" />

          <div data-id="element-7790">
            <p className="text-sm font-medium text-gray-900" data-id="element-7791">
              {t('components.renderSchedulePreview.automatic_link_delivery')}
            </p>
            <p className="text-xs text-gray-600 mt-0.5" data-id="element-7792">
              {t('components.renderSchedulePreview.payment_links_will_be_sent_automatically')}
            </p>
          </div>
        </div>
      </div>
    </div>;
  // ─── COMMISSION PREVIEW ───
  const renderCommissionPreview = () => <div className="space-y-4" data-id="element-7793">
      <p className="text-sm text-gray-600" data-id="element-7794">
        {t('components.renderCommissionPreview.review_the_commission_breakdown_before_creating')}
      </p>

      {/* Total amount card */}
      <div className="bg-gray-50 rounded-xl border border-gray-200 p-5" data-id="element-7795">
        <div className="flex items-center justify-between mb-4" data-id="element-7796">
          <span className="text-sm font-medium text-gray-500" data-id="element-7797">
            {t('components.InstallmentPayment.total_payment_amount')}
          </span>
          <span className="text-xl font-bold text-gray-900" data-id="element-7798">
            {formatCurrencyAmount(totalAmountNum, {
            fractionDigits: 2
          })}
          </span>
        </div>
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-7799">
          <div className="h-full rounded-full bg-gradient-to-r from-[#FFC70A] to-[#FFC70A]/70" style={{
          width: totalAmountNum > 0 ? `${Math.min(totalCommission / totalAmountNum * 100, 100)}%` : '0%'
        }} data-id="element-7800" />
        </div>
        <div className="flex justify-between mt-2 text-xs text-gray-500" data-id="element-7801">
          <span className="flex items-center gap-1" data-id="element-7802">
            <span className="w-2 h-2 rounded-full bg-[#FFC70A]" data-id="element-7803" />
            {t('components.renderCommissionPreview.commission_usd')}{' '}
            {formatCurrencyAmount(totalCommission, {
            fractionDigits: 2
          })}
          </span>
          <span className="flex items-center gap-1" data-id="element-7804">
            <span className="w-2 h-2 rounded-full bg-gray-300" data-id="element-7805" />
            {t('components.renderCommissionPreview.your_share_usd')}{' '}
            {formatCurrencyAmount(businessShare, {
            fractionDigits: 2
          })}
          </span>
        </div>
      </div>

      {/* Supplier breakdown */}
      <div className="border border-gray-200 rounded-xl overflow-hidden" data-id="element-7806">
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200" data-id="element-7807">
          <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7808">
            {t('components.renderCommissionPreview.supplier_commission_breakdown')}
          </h4>
        </div>
        <div className="divide-y divide-gray-100" data-id="element-7809">
          {suppliers.map((supplier, index) => {
          const supplierAmount = commissionType === 'percentage' ? totalAmountNum * (parseFloat(supplier.percentage || '0') / 100) : parseFloat(supplier.amount) || 0;
          const supplierPercentage = commissionType === 'percentage' ? parseFloat(supplier.percentage || '0') : totalAmountNum > 0 ? supplierAmount / totalAmountNum * 100 : 0;
          return <div key={supplier.id} className="px-4 py-4" data-id="element-7810">
                <div className="flex items-center justify-between" data-id="element-7811">
                  <div className="flex items-center gap-3" data-id="element-7812">
                    <div className="w-8 h-8 flex items-center justify-center bg-[#FFC70A]/10 text-[#B28B07] rounded-full font-bold text-xs flex-shrink-0" data-id="element-7813">
                      {index + 1}
                    </div>
                    <div data-id="element-7814">
                      <p className="text-sm font-semibold text-gray-900" data-id="element-7815">
                        {supplier.name || `Supplier ${index + 1}`}
                      </p>
                      {supplier.email && <p className="text-xs text-gray-500" data-id="element-7816">
                          {supplier.email}
                        </p>}
                    </div>
                  </div>
                  <div className="text-right" data-id="element-7817">
                    <p className="text-sm font-bold text-gray-900" data-id="element-7818">
                      {formatCurrencyAmount(supplierAmount, {
                    fractionDigits: 2
                  })}
                    </p>
                    <p className="text-xs text-[#B28B07] font-medium" data-id="element-7819">
                      {supplierPercentage.toFixed(1)}
                      {t('components.renderCommissionPreview.of_total')}
                    </p>
                  </div>
                </div>
              </div>;
        })}
        </div>

        {/* Your business share */}
        <div className="px-4 py-4 bg-gray-50 border-t border-gray-200" data-id="element-7820">
          <div className="flex items-center justify-between" data-id="element-7821">
            <div className="flex items-center gap-3" data-id="element-7822">
              <div className="w-8 h-8 flex items-center justify-center bg-gray-200 text-gray-600 rounded-full flex-shrink-0" data-id="element-7823">
                <BuildingIcon size={14} data-id="element-7824" />
              </div>
              <div data-id="element-7825">
                <p className="text-sm font-semibold text-gray-900" data-id="element-7826">
                  {t('components.renderCommissionPreview.your_business')}
                </p>
                <p className="text-xs text-gray-500" data-id="element-7827">
                  {t('components.renderCommissionPreview.remaining_after_commission')}
                </p>
              </div>
            </div>
            <div className="text-right" data-id="element-7828">
              <p className="text-sm font-bold text-gray-900" data-id="element-7829">
                {formatCurrencyAmount(businessShare, {
                fractionDigits: 2
              })}
              </p>
              <p className="text-xs text-gray-500 font-medium" data-id="element-7830">
                {totalAmountNum > 0 ? (businessShare / totalAmountNum * 100).toFixed(1) : '0.0'}
                {t('components.renderCommissionPreview.of_total')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>;
  // ─── MULTI-RECIPIENT SHARE VIEW ───
  const renderMultiRecipientShare = () => <>
      <div className="flex justify-between items-center mb-4" data-id="element-7831">
        <p className="text-gray-600 font-medium" data-id="element-7832">
          {t('components.renderMultiRecipientShare.send_unique_payment_links_to_each')}
        </p>
      </div>
      <div className="space-y-4 max-h-[50vh] overflow-y-auto" data-id="element-7833">
        {recipients.map((recipient, index) => <div key={recipient.id} className="border border-gray-200 rounded-xl p-4" data-id="element-7834">
            <div className="flex items-center mb-3" data-id="element-7835">
              <div className="w-10 h-10 rounded-full bg-[#FFC70A]/10 flex items-center justify-center flex-shrink-0" data-id="element-7836">
                <UserIcon size={20} className="text-[#B28B07]" aria-hidden="true" data-id="element-7837" />
              </div>
              <div className="ml-3" data-id="element-7838">
                <h4 className="font-medium text-gray-800" data-id="element-7839">
                  {t('components.renderMultiRecipientShare.recipient')}{' '}
                  {index + 1}
                </h4>
                <p className="text-sm text-gray-500" data-id="element-7840">
                  {recipient.email || recipient.phone}
                </p>
              </div>
            </div>

            {/* Toggle */}
            <div className="mb-3" data-id="element-7841">
              <label className="flex items-center cursor-pointer" data-id="element-7842">
                <input type="checkbox" className="sr-only" checked={recipientsViewTotalSettings[recipient.id] || false} onChange={() => toggleRecipientTotalView(recipient.id)} data-id="element-7843" />

                <div className={`w-10 h-5 rounded-full transition-colors ${recipientsViewTotalSettings[recipient.id] ? 'bg-[#FFC70A]' : 'bg-gray-300'} flex items-center p-0.5`} data-id="element-7844">
                  <div className={`w-4 h-4 rounded-full bg-white transform transition-transform ${recipientsViewTotalSettings[recipient.id] ? 'translate-x-5' : ''}`} data-id="element-7845" />
                </div>
                <span className="ml-2 text-sm text-gray-600" data-id="element-7846">
                  {recipientsViewTotalSettings[recipient.id] ? <span className="flex items-center" data-id="element-7847">
                      <DollarSignIcon size={14} className="mr-1" data-id="element-7848" />
                      {t('components.renderMultiRecipientShare.allow_recipient_to_view_the_total')}
                    </span> : <span className="flex items-center" data-id="element-7849">
                      <EyeOffIcon size={14} className="mr-1" data-id="element-7850" />
                      {t('components.renderMultiRecipientShare.recipient_will_only_see_their_portion')}
                    </span>}
                </span>
              </label>
            </div>

            {/* Payment link */}
            <div className="mb-3" data-id="element-7851">
              <div className="flex" data-id="element-7852">
                <input type="text" className="flex-1 px-3 py-2 border border-gray-200 rounded-l-lg focus:ring-#[FFC70A]/20 focus:border-#[FFC70A] bg-gray-50 text-sm" value={`https://payment.example.com/pay/${recipient.id}-abc123${recipientsViewTotalSettings[recipient.id] ? '?showTotal=true' : ''}`} readOnly data-id="element-7853" />

                <button className="px-3 py-2 bg-[#FFC70A] text-white rounded-r-lg hover:bg-[#E5B309] transition-colors flex items-center text-sm font-medium" onClick={() => handleCopyLink(recipient.id)} data-id="element-7854">
                  {copiedRecipientId === recipient.id ? <>
                      <CheckIcon size={14} className="mr-1" data-id="element-7855" />
                      {t('components.renderMultiRecipientShare.copied')}
                    </> : <>
                      <LinkIcon size={14} className="mr-1" data-id="element-7856" />
                      {t('components.renderMultiRecipientShare.copy')}
                    </>}
                </button>
              </div>
            </div>

            {/* Contact options */}
            {(recipient.email || recipient.phone) && <div className="mt-3" data-id="element-7857">
                <h5 className="text-xs font-medium text-gray-500 mb-2" data-id="element-7858">
                  {t('components.renderMultiRecipientShare.send_payment_link_via')}
                </h5>
                <div className="flex space-x-2" data-id="element-7859">
                  {recipient.email && recipient.phone && <>
                      <button className={`flex-1 py-2 px-3 rounded-lg flex items-center justify-center text-sm font-medium transition-colors ${getContactMethod(recipient) === 'email' ? 'bg-[#FFC70A]/10 text-[#FFC70A] border border-[#FFC70A]/30' : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'}`} onClick={() => setContactMethod(recipient.id, 'email')} data-id="element-7860">
                        <MailIcon size={14} className="mr-1.5" data-id="element-7861" />
                        {t('components.RecurringPayment.email')}
                      </button>
                      <button className={`flex-1 py-2 px-3 rounded-lg flex items-center justify-center text-sm font-medium transition-colors ${getContactMethod(recipient) === 'sms' ? 'bg-[#FFC70A]/10 text-[#FFC70A] border border-[#FFC70A]/30' : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'}`} onClick={() => setContactMethod(recipient.id, 'sms')} data-id="element-7862">
                        <SmartphoneIcon size={14} className="mr-1.5" data-id="element-7863" />
                        {t('components.renderMultiRecipientShare.sms')}
                      </button>
                      <button className={`flex-1 py-2 px-3 rounded-lg flex items-center justify-center text-sm font-medium transition-colors ${getContactMethod(recipient) === 'both' ? 'bg-[#FFC70A] text-white border border-[#FFC70A]' : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'}`} onClick={() => setContactMethod(recipient.id, 'both')} data-id="element-7864">
                        {t('components.renderMultiRecipientShare.both')}
                      </button>
                    </>}
                  {recipient.email && !recipient.phone && <button className="flex-1 py-2 px-3 rounded-lg flex items-center justify-center text-sm font-medium bg-[#FFC70A]/10 text-[#FFC70A] border border-[#FFC70A]/30 cursor-default" data-id="element-7865">
                      <MailIcon size={14} className="mr-1.5" data-id="element-7866" />
                      {t('components.renderMultiRecipientShare.email_only')}
                    </button>}
                  {!recipient.email && recipient.phone && <button className="flex-1 py-2 px-3 rounded-lg flex items-center justify-center text-sm font-medium bg-[#FFC70A]/10 text-[#FFC70A] border border-[#FFC70A]/30 cursor-default" data-id="element-7867">
                      <SmartphoneIcon size={14} className="mr-1.5" data-id="element-7868" />
                      {t('components.renderMultiRecipientShare.sms_only')}
                    </button>}
                </div>
              </div>}

            {/* Display contact info */}
            <div className="mt-3 text-sm space-y-1" data-id="element-7869">
              {(getContactMethod(recipient) === 'email' || getContactMethod(recipient) === 'both') && recipient.email && <div className="flex items-center text-gray-600" data-id="element-7870">
                    <MailIcon size={14} className="mr-1.5" data-id="element-7871" />
                    {recipient.email}
                  </div>}
              {(getContactMethod(recipient) === 'sms' || getContactMethod(recipient) === 'both') && recipient.phone && <div className="flex items-center text-gray-600" data-id="element-7872">
                    <SmartphoneIcon size={14} className="mr-1.5" data-id="element-7873" />
                    {recipient.phone}
                  </div>}
            </div>
          </div>)}
      </div>
    </>;
  // ─── STANDARD SHARE VIEW (single link) ───
  const renderStandardShare = () => <>
      <p className="text-gray-600 mb-4" data-id="element-7874">
        {t('components.renderStandardShare.choose_how_you_want_to_share')}
      </p>
      <div className="space-y-3" data-id="element-7875">
        {/* Email option */}
        <div className={`border rounded-xl p-4 cursor-pointer transition-all ${selectedOption === 'email' ? 'border-[#FFC70A] bg-[#FFC70A]/[0.03] ring-1 ring-[#FFC70A]' : 'border-gray-200 hover:border-gray-300'}`} onClick={() => setSelectedOption('email')} data-id="element-7876">
          <div className="flex items-center" data-id="element-7877">
            <div className="w-10 h-10 rounded-full bg-[#FFC70A]/10 flex items-center justify-center flex-shrink-0" data-id="element-7878">
              <MailIcon size={20} className="text-[#B28B07]" data-id="element-7879" />
            </div>
            <div className="ml-4 flex-1" data-id="element-7880">
              <h4 className="font-medium text-gray-800" data-id="element-7881">
                {t('components.renderStandardShare.send_via_email')}
              </h4>
              <p className="text-sm text-gray-500 mt-0.5" data-id="element-7882">
                {t('components.renderStandardShare.send_the_payment_link_to_their')}
              </p>
            </div>
          </div>
          {selectedOption === 'email' && <div className="mt-3 pt-3 border-t border-gray-200" data-id="element-7883">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-7884">
                {t('components.InstallmentPayment.attrs.email_address')}
              </label>
              <input type="email" className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-#[FFC70A]/20 focus:border-#[FFC70A] transition-colors" placeholder={t('components.renderStandardShare.attrs.nameexamplecom')} value={recipientInput} onChange={e => setRecipientInput(e.target.value)} data-id="element-7885" />
            </div>}
        </div>

        {/* SMS option */}
        <div className={`border rounded-xl p-4 cursor-pointer transition-all ${selectedOption === 'sms' ? 'border-[#FFC70A] bg-[#FFC70A]/[0.03] ring-1 ring-[#FFC70A]' : 'border-gray-200 hover:border-gray-300'}`} onClick={() => setSelectedOption('sms')} data-id="element-7886">
          <div className="flex items-center" data-id="element-7887">
            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0" data-id="element-7888">
              <SmartphoneIcon size={20} className="text-green-600" data-id="element-7889" />
            </div>
            <div className="ml-4 flex-1" data-id="element-7890">
              <h4 className="font-medium text-gray-800" data-id="element-7891">
                {t('components.renderStandardShare.send_via_text_message')}
              </h4>
              <p className="text-sm text-gray-500 mt-0.5" data-id="element-7892">
                {t('components.renderStandardShare.send_the_payment_link_to_their_2')}
              </p>
            </div>
          </div>
          {selectedOption === 'sms' && <div className="mt-3 pt-3 border-t border-gray-200" data-id="element-7893">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-7894">
                {t('components.InstallmentPayment.attrs.phone_number')}
              </label>
              <input type="tel" className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-#[FFC70A]/20 focus:border-#[FFC70A] transition-colors" placeholder="+1 (555) 123-4567" value={recipientInput} onChange={e => setRecipientInput(e.target.value)} data-id="element-7895" />
            </div>}
        </div>

        {/* QR Code option */}
        <div className={`border rounded-xl p-4 cursor-pointer transition-all ${selectedOption === 'qrcode' ? 'border-[#FFC70A] bg-[#FFC70A]/[0.03] ring-1 ring-[#FFC70A]' : 'border-gray-200 hover:border-gray-300'}`} onClick={() => setSelectedOption('qrcode')} data-id="element-7896">
          <div className="flex items-center" data-id="element-7897">
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0" data-id="element-7898">
              <QrCodeIcon size={20} className="text-amber-600" data-id="element-7899" />
            </div>
            <div className="ml-4 flex-1" data-id="element-7900">
              <h4 className="font-medium text-gray-800" data-id="element-7901">
                {t('components.renderStandardShare.generate_qr_code')}
              </h4>
              <p className="text-sm text-gray-500 mt-0.5" data-id="element-7902">
                {t('components.renderStandardShare.create_a_scannable_qr_code_for')}
              </p>
            </div>
          </div>
          {selectedOption === 'qrcode' && <div className="mt-3 pt-3 border-t border-gray-200" data-id="element-7903">
              <div className="flex flex-col items-center" data-id="element-7904">
                <div ref={qrCodeRef} className="bg-white p-4 rounded-lg mb-3" data-id="element-7905">
                  <QRCodeSVG value="https://payment.example.com/pay/abc123" size={200} level="H" includeMargin={true} data-id="element-7906" />
                </div>
                <button className="px-4 py-2 bg-[#FFC70A] text-white rounded-lg hover:bg-[#E5B309] transition-colors flex items-center font-medium text-sm" onClick={handleDownloadQR} data-id="element-7907">
                  {qrDownloaded ? <>
                      <CheckIcon size={16} className="mr-2" data-id="element-7908" />
                      {t('components.renderStandardShare.downloaded')}
                    </> : <>
                      <DownloadIcon size={16} className="mr-2" data-id="element-7909" />
                      {t('components.renderStandardShare.download_qr_code')}
                    </>}
                </button>
                <p className="text-xs text-gray-500 mt-2 text-center" data-id="element-7910">
                  {t('components.renderStandardShare.customers_can_scan_this_qr_code')}
                </p>
              </div>
            </div>}
        </div>

        {/* Copy link option */}
        <div className={`border rounded-xl p-4 cursor-pointer transition-all ${selectedOption === 'copy' ? 'border-[#FFC70A] bg-[#FFC70A]/[0.03] ring-1 ring-[#FFC70A]' : 'border-gray-200 hover:border-gray-300'}`} onClick={() => setSelectedOption('copy')} data-id="element-7911">
          <div className="flex items-center" data-id="element-7912">
            <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0" data-id="element-7913">
              <LinkIcon size={20} className="text-purple-600" data-id="element-7914" />
            </div>
            <div className="ml-4 flex-1" data-id="element-7915">
              <h4 className="font-medium text-gray-800" data-id="element-7916">
                {t('components.renderStandardShare.copy_link_to_share')}
              </h4>
              <p className="text-sm text-gray-500 mt-0.5" data-id="element-7917">
                {t('components.renderStandardShare.copy_the_payment_link_to_share')}
              </p>
            </div>
          </div>
          {selectedOption === 'copy' && <div className="mt-3 pt-3 border-t border-gray-200" data-id="element-7918">
              <div className="flex" data-id="element-7919">
                <input type="text" className="flex-1 px-3 py-2.5 border border-gray-200 rounded-l-lg focus:ring-#[FFC70A]/20 focus:border-#[FFC70A] bg-gray-50 text-sm" value="https://payment.example.com/pay/abc123" readOnly data-id="element-7920" />

                <button className="px-4 py-2.5 bg-[#FFC70A] text-white rounded-r-lg hover:bg-[#E5B309] transition-colors flex items-center font-medium text-sm" onClick={() => handleCopyLink()} data-id="element-7921">
                  {copied ? <>
                      <CheckIcon size={16} className="mr-1" data-id="element-7922" />
                      {t('components.renderMultiRecipientShare.copied')}
                    </> : <>
                      <LinkIcon size={16} className="mr-1" data-id="element-7923" />
                      {t('components.renderMultiRecipientShare.copy')}
                    </>}
                </button>
              </div>
            </div>}
        </div>
      </div>
    </>;
  // ─── FOOTER BUTTONS ───
  const renderFooter = () => {
    if (step === 'preview') {
      return <div className="p-4 border-t border-gray-200 flex justify-between" data-id="element-7924">
          <button className="px-4 py-2.5 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium" onClick={onClose} data-id="element-7925">
            {t('components.CardOnFilePayment.cancel')}
          </button>
          <button className="px-5 py-2.5 bg-[#FFC70A] text-white rounded-lg hover:bg-[#E5B309] transition-colors flex items-center text-sm font-medium" onClick={() => setStep('share')} data-id="element-7926">
            {t('components.renderFooter.continue_to_share')}
            <ArrowRightIcon size={16} className="ml-2" data-id="element-7927" />
          </button>
        </div>;
    }
    // Share step
    const showBackButton = splitType === 'dates' || splitType === 'commission';
    return <div className="p-4 border-t border-gray-200 flex justify-between" data-id="element-7928">
        <div className="flex items-center gap-2" data-id="element-7929">
          {showBackButton && <button className="px-4 py-2.5 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium flex items-center" onClick={() => setStep('preview')} data-id="element-7930">
              <ArrowLeftIcon size={16} className="mr-1.5" data-id="element-7931" />
              {t('components.BulkPaymentPreview.back')}
            </button>}
          <button className="px-4 py-2.5 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium" onClick={onClose} data-id="element-7932">
            {t('components.CardOnFilePayment.cancel')}
          </button>
        </div>
        <div data-id="element-7933">
          {hasMultipleRecipients ? <button className="px-5 py-2.5 bg-[#FFC70A] text-white rounded-lg hover:bg-[#E5B309] transition-colors flex items-center text-sm font-medium" onClick={handleSend} data-id="element-7934">
              <SendIcon size={16} className="mr-2" data-id="element-7935" />
              {t('components.renderFooter.send_to_all_recipients')}
            </button> : <>
              {(selectedOption === 'email' || selectedOption === 'sms') && <button className="px-5 py-2.5 bg-#[#FFC70A] text-white rounded-lg hover:bg-#[#E5B309] transition-colors flex items-center text-sm font-medium disabled:opacity-50" onClick={handleSend} disabled={!recipientInput} data-id="element-7936">
                  <SendIcon size={16} className="mr-2" data-id="element-7937" />
                  {t('components.renderFooter.send')}
                </button>}
              {(selectedOption === 'copy' || selectedOption === 'qrcode') && <button className="px-5 py-2.5 bg-[#FFC70A] text-white rounded-lg hover:bg-[#E5B309] transition-colors text-sm font-medium" onClick={onClose} data-id="element-7938">
                  {t('components.renderFooter.done')}
                </button>}
              {!selectedOption && <button className="px-5 py-2.5 bg-gray-100 text-gray-400 rounded-lg text-sm font-medium cursor-not-allowed" disabled data-id="element-7939">
                  {t('components.renderFooter.select_an_option')}
                </button>}
            </>}
        </div>
      </div>;
  };
  return createPortal(<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100]" data-id="element-7940">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 overflow-hidden" data-id="element-7941">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center" data-id="element-7942">
          <div className="flex items-center gap-3" data-id="element-7943">
            {step === 'preview' && splitType === 'dates' && <div className="w-8 h-8 rounded-lg bg-[#FFC70A]/10 flex items-center justify-center" data-id="element-7944">
                <CalendarIcon size={16} className="text-[#B28B07]" data-id="element-7945" />
              </div>}
            {step === 'preview' && splitType === 'commission' && <div className="w-8 h-8 rounded-lg bg-[#FFC70A]/10 flex items-center justify-center" data-id="element-7946">
                <PercentIcon size={16} className="text-[#B28B07]" data-id="element-7947" />
              </div>}
            <div data-id="element-7948">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-7949">
                {getModalTitle()}
              </h3>
              {step === 'preview' && <div className="flex items-center gap-2 mt-1" data-id="element-7950">
                  <div className="flex items-center gap-1" data-id="element-7951">
                    <div className="w-5 h-5 rounded-full bg-[#FFC70A] text-white flex items-center justify-center text-[10px] font-bold" data-id="element-7952">
                      1
                    </div>
                    <span className="text-xs font-medium text-[#B28B07]" data-id="element-7953">
                      {t('components.ShareLinkModal.review')}
                    </span>
                  </div>
                  <div className="w-4 h-px bg-gray-300" data-id="element-7954" />
                  <div className="flex items-center gap-1" data-id="element-7955">
                    <div className="w-5 h-5 rounded-full bg-gray-200 text-gray-500 flex items-center justify-center text-[10px] font-bold" data-id="element-7956">
                      2
                    </div>
                    <span className="text-xs text-gray-400" data-id="element-7957">
                      {t('components.ShareLinkModal.share')}
                    </span>
                  </div>
                </div>}
              {step === 'share' && (splitType === 'dates' || splitType === 'commission') && <div className="flex items-center gap-2 mt-1" data-id="element-7958">
                    <div className="flex items-center gap-1" data-id="element-7959">
                      <div className="w-5 h-5 rounded-full bg-[#FFC70A] text-white flex items-center justify-center" data-id="element-7960">
                        <CheckIcon size={10} data-id="element-7961" />
                      </div>
                      <span className="text-xs text-gray-400" data-id="element-7962">
                        {t('components.ShareLinkModal.review')}
                      </span>
                    </div>
                    <div className="w-4 h-px bg-[#FFC70A]" data-id="element-7963" />
                    <div className="flex items-center gap-1" data-id="element-7964">
                      <div className="w-5 h-5 rounded-full bg-[#FFC70A] text-white flex items-center justify-center text-[10px] font-bold" data-id="element-7965">
                        2
                      </div>
                      <span className="text-xs font-medium text-[#B28B07]" data-id="element-7966">
                        {t('components.ShareLinkModal.share')}
                      </span>
                    </div>
                  </div>}
            </div>
          </div>
          <button onClick={onClose} aria-label="Close" className="text-gray-400 hover:text-gray-500 transition-colors" data-id="element-7967">
            <XIcon size={20} aria-hidden="true" data-id="element-7968" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 max-h-[65vh] overflow-y-auto" data-id="element-7969">
          {step === 'preview' && splitType === 'dates' && renderSchedulePreview()}
          {step === 'preview' && splitType === 'commission' && renderCommissionPreview()}
          {step === 'share' && hasMultipleRecipients && renderMultiRecipientShare()}
          {step === 'share' && !hasMultipleRecipients && renderStandardShare()}
        </div>

        {/* Footer */}
        {renderFooter()}
      </div>
    </div>, document.body);
}